// Value + merged type: the generated api-backend client re-exports this name as a
// runtime value, so a bare `type` alias breaks the frontend (Rollup) bundle.
export const MaintenancePeriodUnit = {
    days: 'days',
    weeks: 'weeks',
    months: 'months',
} as const;
export type MaintenancePeriodUnit = (typeof MaintenancePeriodUnit)[keyof typeof MaintenancePeriodUnit];

export const MaintenanceScheduleKind = {
    interval: 'interval',
    monthlyPlan: 'monthly_plan',
} as const;
export type MaintenanceScheduleKind = (typeof MaintenanceScheduleKind)[keyof typeof MaintenanceScheduleKind];

// The schedule is a discriminated union so new rule kinds (season blocks, fixed dates,
// rrule, …) can be added without schema changes — extensibility lives in this shape.
export interface IMaintenanceIntervalSchedule {
    kind: 'interval';
    periodAmount: number;
    periodUnit: MaintenancePeriodUnit;
}

// One explicitly pinned visit: which month (0-11, January = 0, aligned with visitsPerMonth),
// which week (1-5, 5 = clamped to the end of short months) and optionally which weekday
// (ISO: 1 = Monday … 7 = Sunday; absent = mid-week). Kept flat (month on each slot) so the
// generated API clients handle it — nested nullable arrays break the codegen templates.
export interface IMaintenanceMonthlySlot {
    month: number;
    week: number;
    weekday?: number | null;
}

export interface IMaintenanceMonthlyPlanSchedule {
    kind: 'monthly_plan';
    // 12 entries, January first; the number of visits wanted in that month, 0 = excluded.
    // Calendar-anchored: unlike interval mode, dates do not chain off the last visit.
    visitsPerMonth: number[];
    // Optional refinement: explicit slots for some months (a month's slot count must equal its
    // visitsPerMonth entry); months without slots keep the even-spread default.
    slots?: IMaintenanceMonthlySlot[] | null;
}

export type MaintenanceSchedule = IMaintenanceIntervalSchedule | IMaintenanceMonthlyPlanSchedule;

export interface IMaintenanceConfigEntity {
    maintenance_config_id?: number;
    name: string;
    is_active: boolean;
    // Deprecated pair, kept readable for rows written before the schedule column existed.
    // `schedule` is the source of truth; use resolveConfigSchedule() instead of reading these.
    period_amount?: number | null;
    period_unit?: MaintenancePeriodUnit | null;
    schedule?: MaintenanceSchedule | null;
    lookahead_months: number;
    // Interval schedules only: false (default) keeps a single open maintenance per client —
    // nothing new is created while any open one exists, however old it is. True restores the
    // lookahead-fill behavior where multiple future visits can be open at once. Monthly plans
    // ignore this: several open visits per month is their design.
    allow_multiple_open: boolean;
    create_on_new_client: boolean;
    apply_to_all_clients: boolean;
    creation_date?: Date;
    client_ids?: number[];
    // Read-only convenience from the list query, aligned with client_ids.
    client_names?: string[];
}

export function resolveConfigSchedule(
    config: Pick<IMaintenanceConfigEntity, 'schedule' | 'period_amount' | 'period_unit'>,
): MaintenanceSchedule | null {
    if (config.schedule) return config.schedule;
    if (config.period_amount != null && config.period_unit != null) {
        return { kind: 'interval', periodAmount: config.period_amount, periodUnit: config.period_unit };
    }
    return null;
}
